import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { DraftTab } from '@/features/Dashboard/pages/Edit/hooks/useDashboardTabs';
import {
  MarketApp,
  MarketApps,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';
import { useMarketPreview } from '@/hooks/api/market';
import { SetState } from '@/types/helpers';

import {
  addWidgetWithTabs,
  appendTilesFromWidget,
  AppendTilesResult,
  buildPreviewConfig,
  updateTabInApps,
  updateTileInTiles
} from './utils';

type TabDraft = {
  marketTabApps: MarketAppWithTabs[];
  tiles: TileBigNumber[];
};

type Handlers = {
  addWidget: (widget: MarketApp) => AppendTilesResult | void;
  updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => void;
  updateTile: (tileId: string, updatedTile: TileBigNumber) => void;
};

type EditDashboardDraftContextValue = {
  addWidget: (widget: MarketApp) => AppendTilesResult | void;
  updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => void;
  updateTile: (tileId: string, updatedTile: TileBigNumber) => void;
  marketTabApps: MarketAppWithTabs[];
  setMarketTabApps: SetState<MarketAppWithTabs[]>;
  tiles: TileBigNumber[];
  setTiles: SetState<TileBigNumber[]>;
  insertAtIndex: number | null;
  setInsertAtIndex: SetState<number | null>;
  insertAtId: string | null;
  setInsertAtId: SetState<string | null>;
  preview: any;
  isPreviewLoading: boolean;
  isPreviewFetching: boolean;
  setIsDragging: SetState<boolean>;
  dashboardName: string;
  setDashboardName: SetState<string>;
  // New properties for nested structure support
  currentTabName: string;
  setCurrentTabName: SetState<string>;
  currentTabId: string | null;
  setCurrentTabId: SetState<string | null>;
  availableTabs: string[];
  setAvailableTabs: SetState<string[]>;
  initialDashboardName: string;
  // Per-tab drafts (keyed by tab ID)
  tabDrafts: Record<string, TabDraft>;
  setTabDrafts: SetState<Record<string, TabDraft>>;
  initializeTabDraft: (
    tabId: string,
    marketTabApps: MarketAppWithTabs[],
    tiles: TileBigNumber[]
  ) => void;
  getTabDraft: (tabId: string) => TabDraft | undefined;
  getAllTabDrafts: () => Record<string, TabDraft>;
  // Tab management
  draftTabs: DraftTab[];
  setDraftTabs: SetState<DraftTab[]>;
  draftTabNames: string[];
};

const EditDashboardDraftContext = createContext<EditDashboardDraftContextValue | undefined>(
  undefined
);

export const EditDashboardDraftProvider: React.FC<
  React.PropsWithChildren & { initialDashboardName: string }
> = ({ children, initialDashboardName }) => {
  // Per-tab drafts storage (keyed by tab ID)
  const [tabDrafts, setTabDrafts] = useState<Record<string, TabDraft>>({});

  // Current tab's draft (derived from tabDrafts based on currentTabId)
  const [marketTabApps, setMarketTabApps] = useState<MarketAppWithTabs[]>([]);
  const [tiles, setTiles] = useState<TileBigNumber[]>([]);
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null);
  const [insertAtId, setInsertAtId] = useState<string | null>(null);
  const [dashboardName, setDashboardName] = useState<string>(initialDashboardName);

  useEffect(() => {
    setDashboardName(initialDashboardName);
  }, [initialDashboardName]);
  // New state for nested structure support
  const [currentTabName, setCurrentTabName] = useState<string>('Main');
  const [currentTabId, setCurrentTabId] = useState<string | null>(null);
  const [availableTabs, setAvailableTabs] = useState<string[]>(['Main']);
  // Tab management
  const [draftTabs, setDraftTabs] = useState<DraftTab[]>([]);

  // Refs to track current values for saving when tab changes
  const marketTabAppsRef = React.useRef<MarketAppWithTabs[]>(marketTabApps);
  const tilesRef = React.useRef<TileBigNumber[]>(tiles);

  // Keep refs in sync with state
  useEffect(() => {
    marketTabAppsRef.current = marketTabApps;
  }, [marketTabApps]);

  useEffect(() => {
    tilesRef.current = tiles;
  }, [tiles]);

  // Auto-save current tab's draft whenever marketTabApps or tiles change
  useEffect(() => {
    if (currentTabId) {
      setTabDrafts(prev => ({
        ...prev,
        [currentTabId]: {
          marketTabApps,
          tiles
        }
      }));
    }
  }, [currentTabId, marketTabApps, tiles]);

  // Load draft when switching tabs (based on tab ID)
  const previousTabIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    // Only load if tab actually changed
    const isTabChange = currentTabId !== previousTabIdRef.current;

    if (isTabChange) {
      // Save previous tab's draft before switching (use refs to get current values)
      // This ensures we save the latest state even if auto-save hasn't run yet
      if (previousTabIdRef.current) {
        setTabDrafts(prev => ({
          ...prev,
          [previousTabIdRef.current!]: {
            marketTabApps: marketTabAppsRef.current,
            tiles: tilesRef.current
          }
        }));
      }

      // Load new tab's draft based on tab ID
      if (currentTabId) {
        const draft = tabDrafts[currentTabId];
        if (draft) {
          setMarketTabApps(draft.marketTabApps);
          setTiles(draft.tiles);
        } else {
          // Initialize empty draft if tab doesn't have one
          setMarketTabApps([]);
          setTiles([]);
        }
      } else {
        // No tab selected, clear content
        setMarketTabApps([]);
        setTiles([]);
      }

      previousTabIdRef.current = currentTabId;
    } else if (currentTabId && previousTabIdRef.current === null) {
      // First time setting currentTabId - load draft if it exists
      const draft = tabDrafts[currentTabId];
      if (draft) {
        setMarketTabApps(draft.marketTabApps);
        setTiles(draft.tiles);
      }
      previousTabIdRef.current = currentTabId;
    }
  }, [currentTabId, tabDrafts]);

  const previewConfig: MarketApps = useMemo(() => {
    return buildPreviewConfig(tiles, marketTabApps);
  }, [tiles, marketTabApps]);
  const [isDragging, setIsDragging] = useState(false);

  const { data, isLoading, isFetching } = useMarketPreview(previewConfig, !isDragging);

  const handlers: Handlers = useMemo(
    () => ({
      addWidget: (widget: MarketApp): AppendTilesResult | void => {
        if ('type' in widget && widget.type === 'tiles') {
          let result: AppendTilesResult | undefined;
          setTiles(prev => {
            result = appendTilesFromWidget(prev, widget);
            return result.tiles;
          });
          return result;
        }
        if ('tabs' in widget) {
          setMarketTabApps(prev => addWidgetWithTabs(prev, widget, insertAtId));
          // reset after use
          setInsertAtIndex(null);
          setInsertAtId(null);
        }
      },
      updateTab: (appId: string, tabId: string, updatedTab: WidgetV2) => {
        setMarketTabApps(prev => updateTabInApps(prev, appId, tabId, updatedTab));
      },
      updateTile: (tileId: string, updatedTile) => {
        setTiles(prev => updateTileInTiles(prev, tileId, updatedTile));
      }
    }),
    [insertAtId]
  );

  const initializeTabDraft = useCallback(
    (tabId: string, marketTabApps: MarketAppWithTabs[], tiles: TileBigNumber[]) => {
      setTabDrafts(prev => ({
        ...prev,
        [tabId]: {
          marketTabApps,
          tiles
        }
      }));
      // If this is the current tab, also update the current state
      if (tabId === currentTabId) {
        setMarketTabApps(marketTabApps);
        setTiles(tiles);
      }
    },
    [currentTabId]
  );

  const getTabDraft = useCallback(
    (tabId: string): TabDraft | undefined => {
      return tabDrafts[tabId];
    },
    [tabDrafts]
  );

  const getAllTabDrafts = useCallback((): Record<string, TabDraft> => {
    // Ensure current tab's draft is saved before returning all drafts
    if (currentTabId) {
      return {
        ...tabDrafts,
        [currentTabId]: {
          marketTabApps,
          tiles
        }
      };
    }
    return tabDrafts;
  }, [tabDrafts, currentTabId, marketTabApps, tiles]);

  // Derive draftTabNames from draftTabs
  const draftTabNames = useMemo(() => draftTabs.map(t => t.name), [draftTabs]);

  const value = useMemo<EditDashboardDraftContextValue>(
    () => ({
      addWidget: handlers.addWidget,
      updateTab: handlers.updateTab,
      updateTile: handlers.updateTile,
      marketTabApps,
      setMarketTabApps,
      tiles,
      setTiles,
      insertAtIndex,
      setInsertAtIndex,
      insertAtId,
      setInsertAtId,
      preview: data,
      isPreviewLoading: isLoading,
      isPreviewFetching: isFetching,
      setIsDragging,
      dashboardName,
      setDashboardName,
      // New properties for nested structure support
      currentTabName,
      setCurrentTabName,
      currentTabId,
      setCurrentTabId,
      availableTabs,
      setAvailableTabs,
      initialDashboardName,
      // Per-tab drafts
      tabDrafts,
      setTabDrafts,
      initializeTabDraft,
      getTabDraft,
      getAllTabDrafts,
      // Tab management
      draftTabs,
      setDraftTabs,
      draftTabNames
    }),
    [
      handlers,
      marketTabApps,
      tiles,
      insertAtIndex,
      insertAtId,
      data,
      isLoading,
      isFetching,
      dashboardName,
      currentTabName,
      currentTabId,
      availableTabs,
      initialDashboardName,
      tabDrafts,
      initializeTabDraft,
      getTabDraft,
      getAllTabDrafts,
      draftTabs,
      draftTabNames
    ]
  );

  return (
    <EditDashboardDraftContext.Provider value={value}>
      {children}
    </EditDashboardDraftContext.Provider>
  );
};

export const useEditDashboardDraft = () => {
  const ctx = useContext(EditDashboardDraftContext);
  if (!ctx) throw new Error('useEditDashboardDraft must be used within EditDashboardDraftProvider');
  return ctx;
};
